/**
 * Destinataires de transfert (WKF-013 §7.4) — collègues + « derniers utilisés » (MRU).
 *
 * Sur les gros tenants (~80 utilisateurs), le sous-menu rapide ne montre que les
 * derniers destinataires (recent-first, complétés par ordre alphabétique), et la
 * modale « Plus d'options » offre une recherche sur toute la liste. Le MRU est
 * persisté dans `user_preferences.recent_assignees` (même rail que task_calendar,
 * remplacement en bloc, sans migration).
 */

import { useCallback, useMemo } from 'react'

import { useAuth } from '@/contexts/AuthContext'
import { useColleagues, type Colleague } from '@/hooks/useAbsences'

const MRU_CAP = 10

export function useTransferRecipients(entityId: string | null) {
    const { user, updatePreferences } = useAuth()
    const { colleagues } = useColleagues(null, entityId)
    const recentIds = useMemo(() => user?.preferences?.recent_assignees ?? [], [user])

    // Collègues ordonnés : derniers utilisés (encore valides) d'abord, puis le reste.
    const ordered: Colleague[] = useMemo(() => {
        const byId = new Map(colleagues.map((c) => [c.id, c]))
        const recent = recentIds.map((id) => byId.get(id)).filter((c): c is Colleague => Boolean(c))
        const seen = new Set(recent.map((c) => c.id))
        const rest = colleagues.filter((c) => !seen.has(c.id))
        return [...recent, ...rest]
    }, [colleagues, recentIds])

    /** Mémorise un destinataire en tête du MRU (dédupliqué, plafonné). */
    const remember = useCallback(
        (id: string) => {
            const next = [id, ...recentIds.filter((x) => x !== id)].slice(0, MRU_CAP)
            void updatePreferences({ recent_assignees: next })
        },
        [recentIds, updatePreferences],
    )

    return { colleagues, ordered, remember }
}
